import { type Dispatch, type ReactElement, type SetStateAction, useCallback, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"

import CloseIcon from "@mui/icons-material/Close"
import { IconButton, useMediaQuery, useTheme as useMuiTheme } from "@mui/material"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"

import { Dropzone } from "../../../components/form/Dropzone"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { TrainingDataResponse } from "../../../types/generated/api/TrainingDataResponse"
import { getEnvVar } from "../../../utils/genericHelpers"

type Props = {
  projectId: string
  open: boolean
  setOpen?: Dispatch<SetStateAction<boolean>>
  onDialogClose: () => void
  setUploadedDataId: Dispatch<SetStateAction<string>>
}

// TODO:: refactor all dropzone components into 1
export function UploadCSVDialog(props: Readonly<Props>): ReactElement {
  const { projectId, open, setOpen, onDialogClose, setUploadedDataId } = props

  const MuiTheme = useMuiTheme()
  const theme = getTheme()

  const { t } = useTranslation("TrainingData")

  const maxSize = Number(getEnvVar("DATA_FILE_MAX_SIZE", 2048)) * 1000000 // converting MB -> Bytes

  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
  const [uploadProgress, setUploadProgress] = useState<number>(0)

  const abortControllerRef = useRef<AbortController | null>(null)

  // https://github.com/TanStack/query/discussions/1551
  // Followed the instructions here by F0rro on jan 23 and cleaned up a bit to match our architecture
  const {
    isLoading: isUploadingCSV,
    mutateAsync: uploadCSVAsyncMutation,
    reset,
  } = useMutation<TrainingDataResponse, AxiosError, File>(
    (file: File) => {
      abortControllerRef.current = new AbortController()
      return KonanAPI.uploadTrainingData(projectId, file, setUploadProgress, abortControllerRef.current.signal)
    },
    {
      mutationKey: "uploadCSV",
    },
  )

  const handleUpload = async (): Promise<void> => {
    try {
      if (!acceptedFiles[0]) return

      const trainingResponse = await uploadCSVAsyncMutation(acceptedFiles[0])
      // Invalidate training data to trigger a refresh
      // When tested, this line takes time to execute. Might want to move this
      // lower down the logic
      // Close dialog after CSV has been uploaded
      await queryClient.invalidateQueries(["training-data", projectId])

      setUploadedDataId(trainingResponse?.uuid)
      const displayName = acceptedFiles[0]?.name || t("notifications.defaultTrainingName")
      NotificationUtils.toast(t("notifications.uploadSuccess", { trainingName: displayName }), {
        severity: "success",
      })

      setTimeout(() => {
        onDialogClose()
        handleCloseAndAbort()
        setAcceptedFiles([])
      }, 500)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (!acceptedFiles[0]) return

      const displayName = acceptedFiles[0]?.name || t("notifications.defaultTrainingName")

      if (err?.message === "canceled") {
        NotificationUtils.toast(t("notifications.uploadError", { trainingName: displayName }), {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("notifications.uploadAbort", { trainingName: displayName }), {
          severity: "error",
        })
      }
      handleCloseAndAbort()
    }
  }

  const setSelectedFiles = (file: File[]): void => {
    setAcceptedFiles(file)

    if (file && file.length > 0) {
      handleUpload()
    }
  }

  /**
   * Aborts ongoing upload and resets component state
   */
  const handleCloseAndAbort = useCallback(() => {
    abortControllerRef.current?.abort()
    reset()
    setUploadProgress(0)
    onDialogClose()
    setAcceptedFiles([])
  }, [onDialogClose, reset])

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if ((reason === "backdropClick" || reason === "escapeKeyDown") && isUploadingCSV) {
          setOpen && setOpen(true)
        } else {
          onDialogClose()
        }
      }}
      maxWidth="xs"
      fullWidth
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("sm"))}
      aria-labelledby="upload-csv-dialog-title"
    >
      <DialogTitle className="alt-dialog-header-base" style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h2-bold">{t("upload_dialog.title")}</Typography>
        <IconButton className="close-icon-button" onClick={() => handleCloseAndAbort()} size={"small"}>
          <CloseIcon style={{ color: theme.palette.base.text[2] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent className="dialog-content-base">
        <Dropzone
          uploadingCSV={isUploadingCSV}
          uploadProgress={uploadProgress}
          resetProgress={() => setUploadProgress(0)}
          selectedFiles={acceptedFiles}
          bottomText={t("upload_dialog.bottomText")}
          setSelectedFiles={setSelectedFiles}
          maxSize={maxSize}
          handleCancelUpload={handleCloseAndAbort}
        />
      </DialogContent>
    </Dialog>
  )
}
